import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LastVisitedApiService } from '@simOn/user/last-visited/data-access';
import { catchError, concatMap, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as UsersActions from './users.actions';
import { selectUsersEntities } from './users.selectors';

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private lastVisitedApiService = inject(LastVisitedApiService);

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.initUsers),
      switchMap(() => of(UsersActions.loadUsersSuccess({ users: [] }))),
      catchError((error) => {
        console.error('Error', error);
        return of(UsersActions.loadUsersFailure({ error }));
      })
    )
  );

  addUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.AddApartmentUserState),
      switchMap((action) =>
        this.lastVisitedApiService.getUsersInApartment$().pipe(
          map((apartmentUsers) => {
            const apartmentUser = {
              ...apartmentUsers.find((user) => user.id === action.apartmentUser.userId)!,
              isActive: false
            };
            return UsersActions.AddApartmentUserStateSuccess({ apartmentUser });
          }),
          catchError((e) => {
            return of(UsersActions.AddApartmentUserStateFailure(e));
          })
        )
      )
    );
  });
  // UpdateApartmentUserState is a no-op in the reducer when the user is not in
  // the store yet (updateOne), so fetch and upsert the full user in that case.
  hydrateUpdatedUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.UpdateApartmentUserState),
      withLatestFrom(this.store.select(selectUsersEntities)),
      filter(([action, entities]) => !entities[action.userState.id]),
      concatMap(([action]) =>
        this.lastVisitedApiService.getUsersInApartment$().pipe(
          map((apartmentUsers) => {
            const apartmentUser = {
              ...apartmentUsers.find((user) => user.id === action.userState.id)!,
              ...action.userState
            };
            return UsersActions.AddApartmentUserStateSuccess({ apartmentUser });
          }),
          catchError((e) => {
            return of(UsersActions.AddApartmentUserStateFailure(e));
          })
        )
      )
    );
  });

  setApartmentUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.SetSelectedApartmentUsers),
      switchMap((action) =>
        this.lastVisitedApiService.getUsersInApartment$().pipe(
          map((apartmentUsers) => {
            const selectedApartmentUsers = apartmentUsers.map((user) => {
              return {
                ...user,
                isActive: action.selectedApartmentUsers.userIds.find((userId) => userId === user.id) ? true : false
              };
            });
            return UsersActions.SetSelectedApartmentUsersSuccess({ selectedApartmentUsers });
          }),
          catchError((e) => {
            return of(UsersActions.SetSelectedApartmentUsersFailure(e));
          })
        )
      )
    );
  });
}
